/**
 * Utilities for formatting and downloading CSV data in the dashboard.
 */

export function downloadCsv<T extends Record<string, unknown>>(
  filename: string,
  data: T[],
  columns?: Array<{ key: keyof T; header: string }>
): void {
  if (!data || data.length === 0) return;

  const cols =
    columns ??
    (Object.keys(data[0]).map((key) => ({
      key: key as keyof T,
      header: key,
    })) as Array<{ key: keyof T; header: string }>);

  const headerRow = cols.map((col) => escapeCsvValue(col.header)).join(",");

  const bodyRows = data.map((row) =>
    cols
      .map((col) => {
        const val = row[col.key];
        return escapeCsvValue(val);
      })
      .join(",")
  );

  const csvContent = [headerRow, ...bodyRows].join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename.endsWith(".csv") ? filename : `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function escapeCsvValue(value: unknown): string {
  if (value === null || value === undefined) return '""';
  let str = typeof value === "object" ? JSON.stringify(value) : String(value);

  // Prevent CSV injection in Excel
  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str;
  }

  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}
